import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
	Form, ControlledSubmitButton, Textarea, Label
} from 'components/Form';
import { Modal, OverlayContent, useOverlayState } from 'components/Overlay';
import { Button } from 'components/Button';
import UserCollectionForm from 'features/productReviews/components/UserCollectionForm';
import { uniqueId } from 'utilities/dom';
import { utagLinkPDP } from 'features/tealium';

import { MAX_CHAR } from '../config';
import { addReply, registerUserEmail } from '../slice';

import './ProductSubmitReplyForm.scss';

import useCharOverAndAvailCount from './useCharOverAndAvailCount';

const ProductSubmitReplyForm = ({
	answerId,
	replyTo,
	onSubmit,
	onCancel,
	parentReplyId = null,
	fromSearch = false,
	pageNumber,
	questionPos
}) => {

	const { createReplyStatus } = useSelector((state) => state.productQuestionsAndAnswers);

	const charOverAndAvailCount = useCharOverAndAvailCount();
	const [ disabledSubmit, setDisabledSubmit ] = useState(true);
	const [ reply, setReply ] = useState({});
	const [ textAreaValue, setTextAreaValue ] = useState('');
	const createThankYouReplyOverlay = useOverlayState(false);

	const dispatch = useDispatch();

	const replyInputId = uniqueId();

	const trackCharacterOver = (overCount) => {

		if (fromSearch || overCount === 0 || parentReplyId !== null) return;

		const charOverMessage = overCount === 1 ? '1 character over' : `${overCount} characters over`;

		utagLinkPDP('TurnTo')({
			eventAction: `Q&A-ReplyError-${charOverMessage}`,
			eventLabel: `${pageNumber}-${questionPos}`
		});

	};

	const handleReplyInputChange = (e) => {

		const input = e.target.value;
		setTextAreaValue(input);

		// if input value is blank and only has spaces, disable submit button
		if (input.trim() === '') {

			charOverAndAvailCount.setCountWhenInputBlank();
			setDisabledSubmit(true);

		} else if (input.length > MAX_CHAR) {

			charOverAndAvailCount.setCountWhenInputOverMax(input);
			setDisabledSubmit(true);
			trackCharacterOver(input.length - MAX_CHAR);

		} else {

			const availCount = MAX_CHAR - input.length;

			charOverAndAvailCount.setCountWhenInputLessThanMax(availCount);
			setDisabledSubmit(availCount === MAX_CHAR);

		}

	};

	const handleSubmit = (data) => {

		const formData = { ...data };

		// Submit prop is not necessary for submission of the form
		if (typeof formData.Submit === 'undefined') {

			delete formData.Submit;

		}

		setReply(formData[replyInputId].trim());
		createThankYouReplyOverlay.showOverlay();

		if (!fromSearch && pageNumber && questionPos) {

			utagLinkPDP('TurnTo')({
				eventAction: 'Q&A-replySubmit',
				eventLabel: `${pageNumber}-${questionPos}`
			});

		}

	};

	async function proccessSubmitReply (collectionData) {

		// register user
		dispatch(registerUserEmail({ ...collectionData }));

		const replyPayload = {
			answerId,
			text: reply,
			user: {
				firstName: collectionData.firstName,
				lastName: collectionData.lastName,
				emailAddress: collectionData.emailAddress
			}
		};

		// when replying to reply, pass parent reply id to API
		if (parentReplyId) {

			replyPayload.parentReplyId = parentReplyId;

		}

		dispatch(addReply(replyPayload));
		createThankYouReplyOverlay.hideOverlay();
		onSubmit();

		utagLinkPDP('TurnTo')({
			eventAction: 'Q&A-Submit-Reply-Collection-Form'
		});

	}

	return (
		<div className="ProductSubmitReplyForm">
			<Form onSubmit={handleSubmit}>
				<Label htmlFor={replyInputId} visuallyHidden>
					Enter a reply to
					{' '}
					{replyTo}
				</Label>
				<Textarea
					id={replyInputId}
					name={replyInputId}
					value={textAreaValue}
					rows="5"
					placeholder={`Enter a reply to ${replyTo}`}
					onChange={handleReplyInputChange}
				/>
				{charOverAndAvailCount.availCharCount <= 200 && charOverAndAvailCount.charOver === 0 && (
					<span className="ProductSubmitReplyForm__charactersLeft">
						{charOverAndAvailCount.availCharCount}
						{' '}
						{charOverAndAvailCount.availCharCount === 1 ? 'character left' : 'characters left'}
					</span>
				)}
				{charOverAndAvailCount.charOver > 0 && (
					<span className="ProductSubmitReplyForm__charactersOver">
						{charOverAndAvailCount.charOver}
						{' '}
						{charOverAndAvailCount.charOver === 1 ? 'character over' : 'characters over'}
					</span>
				)}
				<div className="ProductSubmitReplyForm__disclaimerWrapper">
					<span className="ProductSubmitReplyForm__disclaimerIcon lpIcon-alert" />
					<span className="ProductSubmitReplyForm__disclaimerText">
						Do not include HTML, links, references to other stores, pricing or contact info.
					</span>
				</div>
				<div className="ProductSubmitReplyForm__btnWrapper">
					<ControlledSubmitButton
						name="Submit"
						disabled={createReplyStatus === 'pending' || disabledSubmit}
						buttonStyleVariant="secondary"
					>
						Reply
					</ControlledSubmitButton>
					<Button
						buttonStyleVariant="white"
						onClick={() => onCancel()}
						aria-disabled={createReplyStatus === 'pending'}
					>
						Cancel
					</Button>
				</div>
			</Form>
			<Modal {...createThankYouReplyOverlay} aria-label="Thank you For Your Reply" escapeExits>
				<OverlayContent>
					<div className="ProductSubmitReplyForm__submitReplyModal">
						<UserCollectionForm
							title="Thank You For Your Reply"
							topCopy="Please verify your information so we can post your reply."
							bottomCopy="If you uncheck this option, you may still receive emails related to your reply."
							source="ProductReply"
							onSubmit={proccessSubmitReply}
						/>
					</div>
				</OverlayContent>
			</Modal>
		</div>
	);

};

ProductSubmitReplyForm.propTypes = {
	answerId: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
	replyTo: PropTypes.string,
	parentReplyId: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
	onCancel: PropTypes.func,
	onSubmit: PropTypes.func,
	fromSearch: PropTypes.bool,
	pageNumber: PropTypes.number,
	questionPos: PropTypes.number,
	questionsData: PropTypes.object
};

export default ProductSubmitReplyForm;
